import { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  BarChart3, 
  Users, 
  Activity, 
  TrendingUp, 
  TrendingDown,
  ArrowUp,
  ArrowDown,
  Calendar
} from 'lucide-react'
import { OrganizationContext } from './AssistPage'

interface AnalyticsData {
  totalSignals: number
  uniqueUsers: number
  avgSessionDuration: number
  bounceRate: number
  topEvents: Array<{ name: string; count: number }>
  dailySignals: Array<{ date: string; count: number }>
}

export function AnalyticsPage() {
  const orgContext = useOutletContext<OrganizationContext>()
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d')

  useEffect(() => {
    const fetchAnalytics = async () => {
      const organizationId = orgContext?.organizationId
      if (!organizationId) {
        setLoading(false)
        return
      }
      
      try {
        // For now, use placeholder data
        // In production, this would aggregate from signals collection
        setAnalytics({
          totalSignals: 12456,
          uniqueUsers: 1234,
          avgSessionDuration: 245, // seconds
          bounceRate: 32.5,
          topEvents: [
            { name: 'page_view', count: 8234 },
            { name: 'button_click', count: 2456 },
            { name: 'form_submit', count: 987 },
            { name: 'purchase', count: 456 },
            { name: 'signup', count: 323 },
          ],
          dailySignals: Array.from({ length: 7 }, (_, i) => ({
            date: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { weekday: 'short' }),
            count: Math.floor(Math.random() * 2000) + 1000
          }))
        })
      } catch (error) {
        console.error('Error fetching analytics:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchAnalytics()
  }, [orgContext?.organizationId, timeRange])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">No Analytics Data</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Start tracking signals to see analytics
        </p>
      </div>
    )
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const maxDailyCount = Math.max(...analytics.dailySignals.map(d => d.count))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BarChart3 className="h-8 w-8" />
          <div>
            <h1 className="text-3xl font-bold">Analytics</h1>
            <p className="text-muted-foreground">
              Track and analyze your signals data
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Signals</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalSignals.toLocaleString()}</div>
            <div className="flex items-center text-xs text-green-600 mt-1">
              <ArrowUp className="h-3 w-3 mr-1" />
              12.5% from last period
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.uniqueUsers.toLocaleString()}</div>
            <div className="flex items-center text-xs text-green-600 mt-1">
              <ArrowUp className="h-3 w-3 mr-1" />
              8.2% from last period
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Session</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDuration(analytics.avgSessionDuration)}</div>
            <div className="flex items-center text-xs text-green-600 mt-1">
              <ArrowUp className="h-3 w-3 mr-1" />
              3.4% from last period
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bounce Rate</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.bounceRate}%</div>
            <div className="flex items-center text-xs text-red-600 mt-1">
              <ArrowDown className="h-3 w-3 mr-1" />
              2.1% from last period
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Daily Signals Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Signals Over Time</CardTitle>
            <CardDescription>Daily signal volume for the selected period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.dailySignals.map((day, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-12 text-sm text-muted-foreground">{day.date}</div>
                  <div className="flex-1">
                    <div 
                      className="h-6 bg-primary/20 rounded-md transition-all relative overflow-hidden"
                      style={{ width: `${(day.count / maxDailyCount) * 100}%` }}
                    >
                      <div 
                        className="absolute inset-0 bg-primary rounded-md"
                        style={{ width: `${(day.count / maxDailyCount) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-16 text-sm text-right font-medium">
                    {day.count.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Events */}
        <Card>
          <CardHeader>
            <CardTitle>Top Events</CardTitle>
            <CardDescription>Most frequently tracked event types</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.topEvents.map((event) => {
                const percentage = (event.count / analytics.totalSignals) * 100
                return (
                  <div key={event.name} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{event.name}</span>
                      <span className="text-muted-foreground">
                        {event.count.toLocaleString()} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Insights</CardTitle>
          <CardDescription>Key observations from your analytics data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
                <TrendingUp className="h-4 w-4" />
                Growing Engagement
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                User engagement has increased by 12.5% compared to the previous period.
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium">
                <Users className="h-4 w-4" />
                Active User Base
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {analytics.uniqueUsers.toLocaleString()} unique users tracked in the selected period.
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 font-medium">
                <Activity className="h-4 w-4" />
                Event Diversity
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {analytics.topEvents.length} different event types tracked across your application.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
